import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadBlob } from '@/utils/download';
import { parseActiveStatusFilter, type ActiveStatusChoice } from '@/utils/filters';
import { DEFAULT_DASHBOARD_PAGE_SIZE } from '../constants/dashboard.constants';
import {
  dcrDashboardResponseSchema,
  derDashboardResponseSchema,
  systemDashboardResponseSchema,
  workSummarySchema,
  type DcrDashboardResponse,
  type DcrDashboardListSearch,
  type DerDashboardResponse,
  type DerDashboardSearch,
  type SystemDashboardResponse,
  type SystemDashboardListSearch,
  type WorkSummary,
} from '../types/dashboard.types';

export interface WorkSummaryRequest {
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
  dcrTypeId?: number | undefined;
  isVerifiedOnly: boolean;
}

interface ZmStatusSearch {
  zmStatus?: ActiveStatusChoice | undefined;
}

function toActiveStatusParams<T extends ZmStatusSearch>({
  zmStatus,
  ...rest
}: T): Omit<T, 'zmStatus'> & { isActive: boolean | undefined } {
  return { ...rest, isActive: parseActiveStatusFilter(zmStatus) };
}

export const dashboardQueryKeys = {
  all: () => ['dashboards'] as const,
  system: (params: SystemDashboardListSearch) =>
    [...dashboardQueryKeys.all(), 'system', params] as const,
  dcr: (params: DcrDashboardListSearch) => [...dashboardQueryKeys.all(), 'dcr', params] as const,
  der: (params: DerDashboardSearch) => [...dashboardQueryKeys.all(), 'der', params] as const,
  workSummary: (userId: number, search: WorkSummaryRequest) =>
    [
      ...dashboardQueryKeys.all(),
      'work-summary',
      userId,
      search.dateFrom,
      search.dateTo,
      search.dcrTypeId,
      search.isVerifiedOnly,
    ] as const,
};

export const systemDashboardInfiniteQueryOptions = (params: SystemDashboardListSearch) =>
  infiniteQueryOptions({
    queryKey: dashboardQueryKeys.system(params),
    queryFn: async ({ pageParam }): Promise<SystemDashboardResponse> => {
      const res = await apiClient.get(API.dashboard.system, {
        params: {
          ...toActiveStatusParams(params),
          page: pageParam,
          pageSize: DEFAULT_DASHBOARD_PAGE_SIZE,
        },
      });
      return systemDashboardResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const dcrDashboardInfiniteQueryOptions = (params: DcrDashboardListSearch) =>
  infiniteQueryOptions({
    queryKey: dashboardQueryKeys.dcr(params),
    queryFn: async ({ pageParam }): Promise<DcrDashboardResponse> => {
      const res = await apiClient.get(API.dashboard.dcr, {
        params: {
          ...toActiveStatusParams(params),
          page: pageParam,
          pageSize: DEFAULT_DASHBOARD_PAGE_SIZE,
        },
      });
      return dcrDashboardResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const derDashboardInfiniteQueryOptions = (params: DerDashboardSearch) =>
  infiniteQueryOptions({
    queryKey: dashboardQueryKeys.der(params),
    queryFn: async ({ pageParam }): Promise<DerDashboardResponse> => {
      const res = await apiClient.get(API.dashboard.der, {
        params: {
          ...toActiveStatusParams(params),
          page: pageParam,
          pageSize: DEFAULT_DASHBOARD_PAGE_SIZE,
        },
      });
      return derDashboardResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const workSummaryQueryOptions = (userId: number | null, search: WorkSummaryRequest) =>
  queryOptions({
    queryKey: dashboardQueryKeys.workSummary(userId ?? 0, search),
    queryFn: async (): Promise<WorkSummary> => {
      const res = await apiClient.get(API.dashboard.workSummary(userId ?? 0), {
        params: search,
      });
      return workSummarySchema.parse(res.data);
    },
    enabled: userId !== null,
    staleTime: STALE_TIME,
  });

async function download(url: string, params: object, name: string): Promise<void> {
  const res = await apiClient.get<Blob>(url, { params, responseType: 'blob' });
  downloadBlob(res.data, name);
}

export const downloadSystemDashboard = (params: SystemDashboardListSearch): Promise<void> =>
  download(API.dashboard.systemExport, toActiveStatusParams(params), 'system-dashboard.xlsx');

export const downloadDerDashboard = (params: DerDashboardSearch): Promise<void> =>
  download(API.dashboard.derExport, toActiveStatusParams(params), 'der-dashboard.xlsx');
